import ReactOnRails from 'react-on-rails';
import store from 'chat/store';
import { resetChatState } from 'chat/store/chat-thunks';
import { resetBrandPromptState } from 'chat/slices/brand-discovery-slice';
import { getChatIdParam, setChatIdParam, removeChatIdParam } from './side-chat-url';
import bridge from './side-chat-bridge';

const chatState = {
  initialized: false,
  toggleBtn: null,
  calendarId: null,
};

const CHAT_CONTENT_ATTRS = [
  'user', 'calendar', 'paths', 'aws', 'permissions',
  'usageLimit', 'usageWarning', 'isOnMaxPlan',
  'isCollaborator', 'ownerNotified', 'creditsResetDate',
  'ctaMode', 'ctaUrl', 'hasBrandInformation'
];

function calendarIdFromUrl() {
  const match = window.location.pathname.match(/^\/calendars\/(\d+)/);
  return match ? Number(match[1]) : null;
}

function applyOpenState(isOpen) {
  const body = document.getElementById('loomly-ai-body');
  if (!body) return;
  body.classList.toggle('chat-open', !!isOpen);
  const toggleBtn = document.getElementById('loomly-ai-chat-toggle');
  if (toggleBtn) toggleBtn.classList.toggle('active', !!isOpen);
}

function mountSideChat() {
  const chatContent = document.getElementById('loomly-ai-chat-content');
  if (!chatContent || chatState.initialized) return;

  const userData = JSON.parse(chatContent.dataset.user);
  const calendarData = chatContent.dataset.calendar ? JSON.parse(chatContent.dataset.calendar) : null;
  const pathsData = chatContent.dataset.paths ? JSON.parse(chatContent.dataset.paths) : {};
  const awsData = chatContent.dataset.aws ? JSON.parse(chatContent.dataset.aws) : null;
  const permissionsData = chatContent.dataset.permissions ? JSON.parse(chatContent.dataset.permissions) : {};

  ReactOnRails.render('AiAssistantChat', {
    context: { user: userData, calendar: calendarData, paths: pathsData, aws: awsData, permissions: permissionsData },
    emptyTitle: "How can I help you today?",
    emptySubtitle: "Ask me anything about your social media content.",
    hasBrandInformation: chatContent.dataset.hasBrandInformation === 'true',
    usageLimitReached: chatContent.dataset.usageLimit === 'true',
    usageWarningReached: chatContent.dataset.usageWarning === 'true',
    isOnMaxPlan: chatContent.dataset.isOnMaxPlan === 'true',
    isCollaborator: chatContent.dataset.isCollaborator === 'true',
    ownerNotified: chatContent.dataset.ownerNotified === 'true',
    creditsResetDate: chatContent.dataset.creditsResetDate || null,
    ctaMode: chatContent.dataset.ctaMode || 'upgrade',
    ctaUrl: chatContent.dataset.ctaUrl || null
  }, 'loomly-ai-chat-content');

  chatState.initialized = true;
  chatState.calendarId = calendarData?.id || calendarIdFromUrl();
}

function unmountSideChat() {
  if (!chatState.initialized) return;
  bridge.clearListeners('prompt');
  store.dispatch(resetChatState());
  store.dispatch(resetBrandPromptState());
  const chatContent = document.getElementById('loomly-ai-chat-content');
  if (chatContent) {
    if (typeof ReactOnRails.unmountComponents === 'function') {
      ReactOnRails.unmountComponents(chatContent);
    }
    chatContent.innerHTML = '';
  }
  chatState.initialized = false;
  chatState.calendarId = null;
}

export function openSideChat() {
  mountSideChat();
  const body = document.getElementById('loomly-ai-body');
  if (!body) return;
  body.classList.add('chat-transitioning');
  body.classList.add('chat-open');
  setTimeout(() => body.classList.remove('chat-transitioning'), 300);
  const toggleBtn = document.getElementById('loomly-ai-chat-toggle');
  if (toggleBtn) toggleBtn.classList.add('active');
}

export function closeSideChat() {
  removeChatIdParam();
  bridge.emit('analyticsContext', null);
  const body = document.getElementById('loomly-ai-body');
  if (!body) return;
  body.classList.add('chat-transitioning');
  body.classList.remove('chat-open');
  setTimeout(() => {
    body.classList.remove('chat-transitioning');
    if (!body.classList.contains('chat-open')) {
      unmountSideChat();
    }
  }, 300);
  const toggleBtn = document.getElementById('loomly-ai-chat-toggle');
  if (toggleBtn) toggleBtn.classList.remove('active');
}

function handleToggleClick(e) {
  e.preventDefault();
  const body = document.getElementById('loomly-ai-body');
  if (!body) return;

  const nowOpen = !body.classList.contains('chat-open');

  if (nowOpen) {
    setChatIdParam('new');
    openSideChat();
  } else {
    closeSideChat();
  }

  if (window.trackPicoUserAction) {
    const uiState = window.getLoomlyAiUiState ? window.getLoomlyAiUiState() : {};
    window.trackPicoUserAction('ai_assistant_toggled', { ...uiState, open: nowOpen });
  }
}

function applyTurboFrameToLinks() {
  const selectors = [
    '#loomly-ai-sidebar a[href]',
    '#loomly-ai-main-frame a[href]'
  ];

  document.querySelectorAll(selectors.join(', ')).forEach((a) => {
    if (a.dataset.turboFrame) return;
    try {
      const url = new URL(a.href, window.location.href);
      if (url.origin === window.location.origin) {
        a.dataset.turboFrame = 'loomly-ai-main-frame';
      }
    } catch { }
  });
}

function syncFromUrl() {
  const pageCalendarId = calendarIdFromUrl();
  if (chatState.initialized && chatState.calendarId && pageCalendarId && pageCalendarId !== chatState.calendarId) {
    removeChatIdParam();
    bridge.emit('analyticsContext', null);
    unmountSideChat();
    applyOpenState(false);
    return;
  }

  const chatId = getChatIdParam();
  const analyticsActive = !!bridge.get('analyticsContext');
  if (chatId || analyticsActive) {
    mountSideChat();
    applyOpenState(true);
  } else {
    unmountSideChat();
    applyOpenState(false);
  }
}

document.addEventListener('turbo:load', () => {
  syncFromUrl();
  applyTurboFrameToLinks();

  const toggleBtn = document.getElementById('loomly-ai-chat-toggle');
  if (toggleBtn && chatState.toggleBtn !== toggleBtn) {
    if (chatState.toggleBtn) {
      chatState.toggleBtn.removeEventListener('click', handleToggleClick);
    }
    toggleBtn.addEventListener('click', handleToggleClick);
    chatState.toggleBtn = toggleBtn;
  }
});

window.addEventListener('popstate', () => {
  syncFromUrl();
});

document.addEventListener('turbo:before-render', (e) => {
  const newBody = e.detail?.newBody;
  if (!newBody) return;
  const incoming = newBody.querySelector('#loomly-ai-chat-content');
  const current = document.getElementById('loomly-ai-chat-content');
  if (!incoming || !current) return;
  CHAT_CONTENT_ATTRS.forEach((attr) => {
    const val = incoming.dataset[attr];
    if (val !== undefined) current.dataset[attr] = val;
    else delete current.dataset[attr];
  });
});

document.addEventListener('turbo:frame-load', (e) => {
  if (e.target.id === 'loomly-ai-main-frame') applyTurboFrameToLinks();
});
